import React, { useMemo, useState } from 'react';
import { differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';
import { es as esLocale } from 'date-fns/locale';
import { CalendarHeartIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { whatsappLink } from '../../utils/whatsapp';
import type { Content } from '../../data/translations';
function guidanceFor(days: number, copy: Content['process']['countdown']['guidance']) {
  if (days < 0) return copy.past;
  if (days <= 3) return copy.rush;
  if (days <= 14) return copy.tight;
  if (days <= 56) return copy.ideal;
  return copy.early;
}
export function EventCountdown() {
  const {
    t,
    lang
  } = useLanguage();
  const c = t.process.countdown;
  const [value, setValue] = useState('');
  const result = useMemo(() => {
    if (!value) return null;
    const date = parseISO(value);
    if (!isValid(date)) return null;
    const days = differenceInCalendarDays(date, new Date());
    return {
      days,
      date,
      ...guidanceFor(days, c.guidance)
    };
  }, [value, c.guidance]);
  return <div className="rounded-2xl border border-petal bg-white p-7 sm:p-8" data-id="element-304" data-mp="1iishme5qnxoj">
      <CalendarHeartIcon className="h-7 w-7 text-mauve" aria-hidden="true" data-id="element-305" data-mp="1dkzaniwm2zn7" />
      <h3 className="mt-5 font-display text-2xl font-semibold text-ink" data-id="element-306" data-mp="18lw9i9jr5dki">{c.title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-ink/70" data-id="element-307" data-mp="13o4g1b3glje7">{c.description}</p>

      <label htmlFor="countdown-date" className="mt-6 block text-xs font-medium uppercase tracking-wider2 text-ink/55" data-id="element-308" data-mp="21bd67jeengmj">
        {c.label}
      </label>
      <input id="countdown-date" type="date" value={value} onChange={e => setValue(e.target.value)} className="mt-2 h-12 w-full rounded-lg border border-petal bg-cream px-4 text-sm text-ink outline-none transition-colors duration-150 ease-smooth focus:border-mauve focus:ring-2 focus:ring-mauve/20" data-id="element-309" data-mp="1wed1bbtakjx0" />

      <div className="mt-6 min-h-[132px] rounded-xl bg-petal/60 p-6" aria-live="polite" data-id="element-310" data-mp="2faw2nexut3j0">
        {result ? <>
            <p className="font-display text-4xl font-semibold text-mauve-deep" data-id="element-311" data-mp="de41ytlwuih3">
              {Math.max(result.days, 0)}
              <span className="ml-2 font-sans text-base font-normal text-ink/60" data-id="element-312" data-mp="ierp3470qf61">
                {Math.abs(result.days) === 1 ? c.day : c.days} {c.away}
              </span>
            </p>
            <p className="mt-3 text-sm font-medium text-mauve-deep" data-id="element-313" data-mp="8l178i5ed2zb">{result.headline}</p>
            <p className="mt-1 text-sm leading-relaxed text-ink/70" data-id="element-314" data-mp="3qqxeeqjdngbj">{result.detail}</p>
            <p className="mt-3 text-xs text-ink/55" data-id="element-315" data-mp="3uuvdj18pcr4l">
              {format(result.date, lang === 'es' ? "EEEE, d 'de' MMMM 'de' yyyy" : 'EEEE, MMMM d, yyyy', lang === 'es' ? {
            locale: esLocale
          } : undefined)}
            </p>
          </> : <p className="text-sm leading-relaxed text-ink/60" data-id="element-316" data-mp="1f3r02qyzlv1x">{c.empty}</p>}
      </div>

      <a href={whatsappLink()} target="_blank" rel="noreferrer" className="mt-6 flex h-12 items-center justify-center rounded-full bg-mauve px-6 text-sm font-medium text-white transition-colors duration-150 ease-smooth hover:bg-mauve-deep" data-id="element-317" data-mp="1k1it5obdbn7p">
        {c.cta}
      </a>
    </div>;
}